import { hasFeature } from 'root/api/admin/features';

import Logger from '../utils/logger';
import { getHubEnabled, setChannelName } from '../utils/storage';
import { AblyClient } from './ablyClient';
import { LocalSocketClient } from './localSocketClient';
import { AbstractSocket, HUB_MESSAGES, isCfdMessage, RetailMessages } from './types';

export class SocketConnectionManager implements AbstractSocket {
  protected localSocket = new LocalSocketClient();

  protected ablySocket = new AblyClient();

  public async connect() {
    if (getHubEnabled()) {
      await this.initChannelName();
    }
    this.ablySocket.connect();
    await this.localSocket.connect();
  }

  public send: AbstractSocket['send'] = async (event: RetailMessages, data?: any) => {
    if (await hasFeature('HubAdvancedLocalServer')) {
      if (isCfdMessage(event) && (await this.ablySocket.isBrowserCFDConnected())) {
        await this.ablySocket.send(event, data);
        return;
      }
      const sentByLocal = await this.localSocket.send(event, data);

      if (!sentByLocal) {
        await this.ablySocket.send(event, data);
      }
    } else {
      await this.ablySocket.send(event, data);
    }
  };

  public disconnect() {
    this.send(HUB_MESSAGES.DISCONNECT);

    this.ablySocket.disconnect();
    this.localSocket.disconnect();
  }

  private async initChannelName(): Promise<void> {
    if (!(await hasFeature('PaymentsTerminalCustomerFacingDisplay'))) return;

    const accountId = window.merchantos.session.getSystemCustomerID();
    const registerId = window.merchantos.session.getRegister()?.registerID;

    if (!accountId || !registerId) return;

    try {
      const registerResponse = await fetch(`/API/Account/${accountId}/Register/${registerId}.json`);
      if (!registerResponse.ok) {
        Logger.error(
          `Failed to fetch register data: ${registerResponse.status} /API/Account/${accountId}/Register/${registerId}.json`
        );
        return;
      }
      const registerData = await registerResponse.json();

      const ccTerminalID = registerData?.Register?.ccTerminalID;
      if (!ccTerminalID) {
        Logger.error('Failed to get ccTerminalID from register data', { accountId, registerId });
        return;
      }

      const terminalResponse = await fetch(
        `/API/Account/${accountId}/CCTerminal/${ccTerminalID}.json`
      );
      if (!terminalResponse.ok) {
        Logger.error(
          `Failed to fetch terminal data: ${terminalResponse.status} /API/Account/${accountId}/CCTerminal/${ccTerminalID}.json`
        );
        return;
      }
      const terminalData = await terminalResponse.json();

      const serialNumber = terminalData?.CCTerminal?.configuration?.serialNumber;
      if (!serialNumber) {
        Logger.error('Failed to get serialNumber from terminal data', { accountId, ccTerminalID });
        return;
      }

      setChannelName(`lspay:terminal:${serialNumber}`);
    } catch (error) {
      Logger.error(error, {
        message: 'Failed to initialize channel name for customer facing display',
      });
    }
  }
}
